import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PACKAGES, getPackageNames } from '../../config/packages';
import { 
  ArrowLeft, 
  Save, 
  Send, 
  User, 
  Building, 
  Mail, 
  Phone, 
  FileText,
  Calendar,
  DollarSign,
  Package,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OfferForm = () => {
  const { offerId } = useParams();
  const isEditing = Boolean(offerId);
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [formData, setFormData] = useState({
    customer_name: '',
    company_name: '',
    customer_email: '',
    customer_phone: '',
    offer_type: 'free-text',
    description: '',
    price: '',
    package_type: '',
    valid_until: '',
    agent_notes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchOffer();
    }
    
    // Set default valid_until to 30 days from now
    if (!isEditing) {
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 30);
      setFormData(prev => ({
        ...prev,
        valid_until: defaultDate.toISOString().split('T')[0]
      }));
    }
  }, [offerId]);

  const fetchOffer = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/offers/${offerId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          customer_name: data.customer_name || '',
          company_name: data.company_name || '',
          customer_email: data.customer_email || '',
          customer_phone: data.customer_phone || '',
          offer_type: data.offer_type || 'free-text',
          description: data.description || '',
          price: data.price || '',
          package_type: data.package_type || '',
          valid_until: data.valid_until ? data.valid_until.split('T')[0] : '',
          agent_notes: data.agent_notes || ''
        });
      }
    } catch (err) {
      setError('Fehler beim Laden des Angebots');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-fill price when package is selected
    if (name === 'package_type' && value && formData.offer_type === 'package') {
      const pkg = PACKAGES[value];
      if (pkg) {
        // Extract first number from price range as suggestion
        const priceMatch = pkg.price.match(/[\d']+/);
        if (priceMatch) {
          const suggestedPrice = priceMatch[0].replace(/'/g, '');
          setFormData(prev => ({
            ...prev,
            package_type: value,
            price: suggestedPrice,
            original_package_price: pkg.price
          }));
        }
      }
    }
  };

  const handleOfferTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      offer_type: type,
      package_type: type === 'free-text' ? '' : prev.package_type,
      description: type === 'package' ? '' : prev.description
    }));
  };

  const handleSubmit = async (action) => {
    setError('');
    setSaving(true);

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        description: formData.offer_type === 'package' 
          ? `${PACKAGES[formData.package_type]?.emoji} ${formData.package_type} Paket` 
          : formData.description
      };

      const url = isEditing 
        ? `${API_URL}/api/offers/${offerId}`
        : `${API_URL}/api/offers/`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Fehler beim Speichern');
      }

      const savedOffer = await response.json();

      if (action === 'send') {
        // Send the offer
        const sendResponse = await fetch(`${API_URL}/api/offers/${savedOffer.id}/send`, {
          method: 'POST',
          headers: getAuthHeaders()
        });

        if (sendResponse.ok) {
          alert('Angebot erfolgreich gesendet!');
        } else {
          const sendData = await sendResponse.json();
          alert(`Angebot gespeichert, aber Versand fehlgeschlagen: ${sendData.detail}`);
        }
      } else {
        alert('Angebot als Entwurf gespeichert!');
      }

      navigate('/admin/offers');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedPackage = formData.package_type ? PACKAGES[formData.package_type] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Laden...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/offers')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isEditing ? 'Angebot bearbeiten' : 'Neues Angebot erstellen'}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-cyan-600" />
                Kundeninformationen
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name">Kundenname *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    placeholder="Max Mustermann"
                    className="pl-10"
                    required
                    data-testid="customer-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company_name">Firmenname</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Muster GmbH"
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customer_email">E-Mail *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    placeholder="kunde@beispiel.ch"
                    className="pl-10"
                    required
                    data-testid="customer-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customer_phone">Telefon</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="customer_phone"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    placeholder="+41 79 123 45 67"
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Offer Type Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-600" />
                Angebotstyp
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => handleOfferTypeChange('free-text')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    formData.offer_type === 'free-text'
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="offer-type-freetext"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.offer_type === 'free-text' ? 'bg-cyan-500 text-white' : 'bg-gray-100'
                    }`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Freitext-Angebot</p>
                      <p className="text-sm text-gray-500">Individuelle Beschreibung</p>
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleOfferTypeChange('package')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    formData.offer_type === 'package'
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="offer-type-package"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.offer_type === 'package' ? 'bg-cyan-500 text-white' : 'bg-gray-100'
                    }`}>
                      <Package className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Paket-Angebot</p>
                      <p className="text-sm text-gray-500">Basierend auf Preispaketen</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Free-text description */}
              {formData.offer_type === 'free-text' && (
                <div className="space-y-2">
                  <Label htmlFor="description">Leistungsbeschreibung *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Beschreiben Sie die Leistungen im Detail..."
                    rows={6}
                    required
                    data-testid="description-textarea"
                  />
                </div>
              )}

              {/* Package selection */}
              {formData.offer_type === 'package' && (
                <div className="space-y-4">
                  <Label>Paket auswählen *</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {getPackageNames().map((pkgName) => {
                      const pkg = PACKAGES[pkgName];
                      return (
                        <button
                          key={pkgName}
                          type="button"
                          onClick={() => handleChange({ target: { name: 'package_type', value: pkgName } })}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            formData.package_type === pkgName
                              ? 'border-cyan-500 bg-cyan-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold">{pkg.emoji} {pkg.name}</p>
                              <p className="text-sm text-gray-500">CHF {pkg.price}</p>
                            </div>
                            {formData.package_type === pkgName && (
                              <CheckCircle className="w-5 h-5 text-cyan-500" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Package Features Preview */}
                  {selectedPackage && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3 text-gray-700">
                        {selectedPackage.emoji} {selectedPackage.name} - Enthaltene Leistungen:
                      </h4>
                      <div className="grid md:grid-cols-2 gap-2 text-sm">
                        {selectedPackage.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span>{feature.icon}</span>
                            <span className="text-gray-600">{feature.title}: {feature.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing & Validity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-cyan-600" />
                Preis & Gültigkeit
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">
                  Angebotspreis (CHF) *
                  {formData.offer_type === 'package' && selectedPackage && (
                    <span className="text-gray-400 text-sm ml-2">
                      (Standardpreis: CHF {selectedPackage.price})
                    </span>
                  )}
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="5000"
                    className="pl-10"
                    required
                    data-testid="price-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="valid_until">Gültig bis *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="valid_until"
                    name="valid_until"
                    type="date"
                    value={formData.valid_until}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="valid-until-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Zusätzliche Notizen (optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                name="agent_notes"
                value={formData.agent_notes}
                onChange={handleChange}
                placeholder="Interne Notizen oder zusätzliche Informationen für den Kunden..."
                rows={4}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/offers')}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSubmit('draft')}
              disabled={saving}
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Als Entwurf speichern'}
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit('send')}
              disabled={saving}
              className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700"
              data-testid="send-offer-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {saving ? 'Senden...' : 'Angebot senden'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default OfferForm;
